/**
 * Keeps AI models and engine binaries on the device once they are downloaded,
 * so people on mobile data pay for them once: a file is kept until a newer one
 * replaces it, or for a year, whichever comes first.
 *
 * Files live in their own Cache Storage cache (readable from windows and
 * workers). Each entry records when it was stored; a file older than a year is
 * downloaded again. Storing a file drops what it replaces: older versions of
 * the same runtime file, models the app no longer uses, and any entry for which
 * `isCurrent` says no.
 */
export const MODEL_CACHE = 'shycut-models-v1';
/**
 * Where the same files were kept before (the service worker's engine cache,
 * transformers.js's own cache): moved over without downloading them again.
 */
export const LEGACY_CACHES = ['shycut-engines', 'transformers-cache'] as const;
export const MODEL_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
const STORED_AT = 'x-shycut-stored-at';

export interface ModelCacheDeps {
  readonly caches?: CacheStorage | undefined;
  readonly fetch: typeof fetch;
  readonly now: () => number;
  /** Whether a cached address is still one the app uses (old model files and versions are dropped). */
  readonly isCurrent: (url: URL) => boolean;
}

/**
 * Which file a cached address is a version of: runtime files sit in a folder
 * per version (`vendor/ort/1.2.3/x.wasm`), Hugging Face files under a revision
 * (`…/resolve/<rev>/onnx/x.onnx`). Two addresses with the same slot are the
 * same file in different versions, so the newer one replaces the older.
 */
export function cacheSlot(raw: string): string {
  const url = new URL(raw);
  const path = url.pathname.replace(/\/vendor\/ort\/[^/]+\//, '/vendor/ort/*/').replace(/\/resolve\/[^/]+\//, '/resolve/*/');
  return `${url.host}${path}`;
}

export const isFresh = (response: Response, now: number, maxAge = MODEL_MAX_AGE_MS): boolean => {
  const stored = Number(response.headers.get(STORED_AT));
  return Number.isFinite(stored) && stored > 0 && now - stored < maxAge;
};

/** A copy of `response` (status, type headers and body) that records when it was stored. */
async function stamped(response: Response, now: number): Promise<Response> {
  const headers = new Headers();
  for (const name of ['content-type', 'etag']) {
    const value = response.headers.get(name);
    if (value) headers.set(name, value);
  }
  const body = await response.arrayBuffer();
  // Readers show progress from the length, so it is always there.
  headers.set('content-length', String(body.byteLength));
  headers.set(STORED_AT, String(now));
  return new Response(body, { status: 200, statusText: 'OK', headers });
}

/** Opens a cache, or null where Cache Storage isn't available (some private modes, insecure pages). */
async function open(caches: CacheStorage | undefined, name: string): Promise<Cache | null> {
  try {
    return caches ? await caches.open(name) : null;
  } catch {
    return null;
  }
}

/** An absolute http(s) address, or null for anything else (transformers.js also asks for local paths). */
function addressOf(request: RequestInfo | URL): URL | null {
  try {
    const url = new URL(request instanceof Request ? request.url : String(request));
    return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
  } catch {
    return null;
  }
}

/**
 * The cache behind {@link fetchCached} and the caption models (it has the
 * `match`/`put` shape transformers.js takes as its custom cache).
 */
export class ModelCache {
  constructor(private readonly deps: ModelCacheDeps) {}

  /** The stored copy of `request`, if it is still fresh; stale or outdated copies are dropped on the way. */
  async match(request: RequestInfo | URL): Promise<Response | undefined> {
    const url = addressOf(request);
    if (!url) return undefined;
    const cache = await open(this.deps.caches, MODEL_CACHE);
    if (!cache) return undefined;
    const hit = await cache.match(url.href);
    if (hit && isFresh(hit, this.deps.now()) && this.deps.isCurrent(url)) return hit;
    if (hit) await cache.delete(url.href);
    return this.adopt(cache, url);
  }

  /** Stores a downloaded file, dropping the versions and models it replaces. */
  async put(request: RequestInfo | URL, response: Response): Promise<void> {
    const url = addressOf(request);
    if (!response.ok || !url) return;
    const cache = await open(this.deps.caches, MODEL_CACHE);
    if (!cache) return;
    try {
      await cache.put(url.href, await stamped(response, this.deps.now()));
    } catch {
      // Out of space or storage blocked: it is simply downloaded again next time.
      return;
    }
    await this.prune(cache, url);
  }

  /** Addresses of every file kept on this device (fresh or not): what using a model would not download again. */
  async kept(): Promise<URL[]> {
    const cache = await open(this.deps.caches, MODEL_CACHE);
    if (!cache) return [];
    try {
      return (await cache.keys()).map((request) => new URL(request.url));
    } catch {
      return [];
    }
  }

  /** Drops entries that a newer version replaced or the app no longer uses. */
  private async prune(cache: Cache, kept: URL): Promise<void> {
    const slot = cacheSlot(kept.href);
    for (const request of await cache.keys()) {
      const url = new URL(request.url);
      if (url.href === kept.href) continue;
      if (cacheSlot(url.href) === slot || !this.deps.isCurrent(url)) await cache.delete(request);
    }
  }

  /** Moves a file cached the old way into this cache, so it isn't downloaded again. */
  private async adopt(cache: Cache, url: URL): Promise<Response | undefined> {
    if (!this.deps.isCurrent(url)) return undefined;
    for (const name of LEGACY_CACHES) {
      const legacy = await open(this.deps.caches, name);
      const old = legacy && (await legacy.match(url.href));
      if (!legacy || !old?.ok) continue;
      try {
        const copy = await stamped(old, this.deps.now());
        await cache.put(url.href, copy.clone());
        await legacy.delete(url.href);
        return copy;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }
}

/**
 * The bytes of a model or engine file: from the device when it has a fresh
 * copy, otherwise downloaded (and kept). `validate` rejects a response that
 * isn't the file (a host answering a missing file with its HTML page).
 */
export async function fetchCached(url: string, cache: ModelCache, deps: Pick<ModelCacheDeps, 'fetch'>, validate: (bytes: Uint8Array) => boolean = () => true): Promise<Uint8Array> {
  const hit = await cache.match(url);
  if (hit) {
    const bytes = new Uint8Array(await hit.arrayBuffer());
    if (validate(bytes)) return bytes;
  }
  const response = await deps.fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (validate(bytes)) await cache.put(url, new Response(bytes, { status: 200, headers: { 'content-type': response.headers.get('content-type') ?? 'application/octet-stream' } }));
  return bytes;
}

/**
 * Asks the browser to keep this site's storage (models included) instead of
 * clearing it when space runs low or after a week without a visit (Safari).
 * Granted silently by most browsers to sites in use; asked once per device.
 */
export async function keepStorage(storage: StorageManager | undefined = globalThis.navigator?.storage): Promise<boolean> {
  try {
    if (!storage?.persist) return false;
    if (await storage.persisted?.()) return true;
    return await storage.persist();
  } catch {
    return false;
  }
}
